/*
 * bc-variant-resolve
 *
 * Shared "variant id from the currently selected options" resolver. Used by
 * bc-variant-optimistic, bc-variant-weight, bc-custom-length and
 * bc-diagram so all four resolve identically (the logic had drifted
 * across four near-duplicate copies).
 *
 * Exposes `window.bcVariantResolve`:
 *   - readOptionValues(scope) → array of the currently selected option values,
 *     in DOM order (so the join key matches Shopify's `variant.options` order)
 *   - resolveVariantId(scope, byOptions) → the matching variant id from the
 *     `byOptions` lookup map (joined option values → id), or null
 *   - findOptionContainer(scope, optionName, {exclude}) → the .variant-option
 *     container whose displayed name matches optionName (exact display-text
 *     match wins over prefix, so "Color" can't bind to "Color Accent"), or null
 *   - readSelectedOption(scope, optionName, {exclude}) → the selected value of
 *     that option ('' when the container has no selection, null when the
 *     option doesn't exist)
 *
 * Walks every `.variant-option` container in DOM order and handles the three
 * shapes Horizon renders: radio fieldsets, dropdown wrappers, and single-value
 * display divs (the locked-in option when only one value exists).
 *
 * IIFE-guarded so multiple block instances can load this asset on the same
 * page without re-defining.
 */
(() => {
  if (window.bcVariantResolve) return;

  function readOptionValues(scope) {
    const values = [];
    if (!scope) return values;
    scope.querySelectorAll('.variant-option').forEach((c) => {
      const checked = c.querySelector?.('input[type="radio"]:checked');
      if (checked) { values.push(checked.value); return; }
      const select = c.querySelector?.('select');
      if (select) { values.push(select.value); return; }
      const single = c.querySelector?.('.variant-option__swatch-value');
      if (single) { values.push(single.textContent.trim()); return; }
    });
    return values;
  }

  function resolveVariantId(scope, byOptions) {
    if (!scope || !byOptions) return null;
    const values = readOptionValues(scope);
    if (!values.length) return null;
    return byOptions[values.join('|')] || null;
  }

  // Shared option-by-display-name lookup (previously hand-rolled in
  // bc-diagrams and bc-variant-recolor, and already drifted). The displayed
  // name is scraped from legend / label / first-text because Horizon doesn't
  // stamp the option name as data; prefix matching stays as a fallback since
  // legends can carry extra content, but an exact match anywhere wins first.
  function findOptionContainer(scope, optionName, { exclude } = {}) {
    if (!scope || !optionName) return null;
    const target = String(optionName).trim().toLowerCase();
    if (!target) return null;

    const candidates = [];
    const push = (el, text) => {
      if (exclude && el.closest?.(exclude)) return;
      const t = (text || '').trim().toLowerCase();
      if (t === target) candidates.push({ el, exact: true });
      else if (t.startsWith(target)) candidates.push({ el, exact: false });
    };

    scope.querySelectorAll('fieldset.variant-option').forEach((fs) => {
      push(fs, fs.querySelector('legend')?.textContent);
    });
    scope.querySelectorAll('.variant-option--dropdowns').forEach((dd) => {
      push(dd, dd.querySelector('label')?.textContent);
    });
    scope.querySelectorAll('.variant-option[data-testid="variant-option-single"]').forEach((node) => {
      push(node, node.firstChild?.textContent);
    });

    const exact = candidates.find((c) => c.exact);
    return (exact || candidates[0])?.el || null;
  }

  function readSelectedOption(scope, optionName, opts) {
    const container = findOptionContainer(scope, optionName, opts);
    if (!container) return null;

    const radio = container.querySelector('input[type="radio"]:checked');
    if (radio) return radio.value;

    const select = container.querySelector('select');
    if (select) return select.value;

    const single = container.querySelector('[data-testid="variant-option-single-value"]');
    if (single) return (single.textContent || '').trim();

    return '';
  }

  window.bcVariantResolve = { readOptionValues, resolveVariantId, findOptionContainer, readSelectedOption };
})();
